// src/pages/Support/SupportDashboard.tsx - FIXED
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supportAPI } from '../../api';
import { Headphones, ShoppingBag, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const SupportDashboard: React.FC = () => {
  const [stats, setStats] = useState({
    openTickets: 0,
    activeOrders: 0,
    totalOrders: 0,
    assignedTickets: 0,
  });
  const [recentTickets, setRecentTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchRecentTickets();
  }, []);

  const fetchStats = async () => {
    try {
      const ticketsRes = await supportAPI.getAllTickets({});
      const ordersRes = await supportAPI.getAllOrders({ limit: 1000 });

      const openTickets = ticketsRes.data.data?.tickets?.filter((t: any) => t.status === 'open').length || 0;
      const activeOrders = ordersRes.data.data?.orders?.filter((o: any) => 
        ['pending', 'accepted', 'in_progress'].includes(o.status)
      ).length || 0;
      const totalOrders = ordersRes.data.data?.orders?.length || 0;
      const assignedTickets = ticketsRes.data.data?.tickets?.filter((t: any) => 
        t.status === 'in_progress' && t.supportAgentId?._id === 'current-user-id'
      ).length || 0;

      setStats({ openTickets, activeOrders, totalOrders, assignedTickets });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentTickets = async () => {
    try {
      const res = await supportAPI.getAllTickets({ limit: 5 });
      if (res.data.success && res.data.data) {
        setRecentTickets(res.data.data.tickets || []);
      }
    } catch (error) {
      console.error('Error fetching recent tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    { icon: <Headphones className="h-6 w-6" />, label: 'Open Tickets', value: stats.openTickets, color: 'from-orange-500 to-red-500', subvalue: 'awaiting response' },
    { icon: <Clock className="h-6 w-6" />, label: 'Active Orders', value: stats.activeOrders, color: 'from-blue-500 to-cyan-500', subvalue: 'in progress' },
    { icon: <ShoppingBag className="h-6 w-6" />, label: 'Total Orders', value: stats.totalOrders, color: 'from-green-500 to-emerald-500', subvalue: 'all time' },
    { icon: <Headphones className="h-6 w-6" />, label: 'Assigned to Me', value: stats.assignedTickets, color: 'from-purple-500 to-pink-500', subvalue: 'your tickets' },
  ];

  if (loading) return <div className="text-center py-12">Loading dashboard...</div>;

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Dashboard</h1>
        <p className="text-gray-600">Welcome back, Support. Here are your current tasks.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        {statsCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`h-10 w-10 lg:h-12 lg:w-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                {React.cloneElement(stat.icon, { className: "h-5 w-5 lg:h-6 lg:w-6 text-white" })}
              </div>
            </div>
            <div className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
            <div className="text-xs lg:text-sm text-gray-600">{stat.label}</div>
            <p className="text-xs text-gray-500 mt-1">{stat.subvalue}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-lg"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Headphones className="h-5 w-5 text-emerald-600" />
            Recent Open Tickets
          </h2>
          <Link to="/support/tickets" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
            View All →
          </Link>
        </div>
        {recentTickets.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No open tickets</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentTickets.slice(0, 5).map((ticket: any) => (
              <Link
                key={ticket._id}
                to={`/support/tickets/${ticket._id}`}
                className="block p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">#{ticket.ticketId}</p>
                    <p className="text-sm text-gray-600 line-clamp-1">{ticket.subject}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    ticket.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                    ticket.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {ticket.priority}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(ticket.createdAt).toLocaleDateString()}
                </p>
              </Link>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SupportDashboard;